import React, { Component, ErrorInfo, ReactNode } from "react";
import { Box, Container, Typography, Paper, Button, Tooltip } from "@mui/material";
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined';

// Define the types for the component's props and state
interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  // Initialize the state
  public state: State = {
    hasError: false,
  };

  /**
   * This lifecycle method is invoked after an error has been thrown by a descendant component.
   * It receives the error that was thrown as a parameter and should return a value to update state.
   * @param _ - The error that was thrown.
   * @returns An object to update the state.
   */
  public static getDerivedStateFromError(_: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  /**
   * This lifecycle method is invoked after an error has been thrown by a descendant component.
   * It is a good place for logging errors.
   * @param error - The error that was thrown.
   * @param errorInfo - An object with a `componentStack` key containing information about which component threw the error.
   */
  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // You can also log the error to an error reporting service
    console.error("Uncaught error:", error, errorInfo);
  }

  /**
   * Renders the component.
   * If an error has occurred, it displays a fallback UI.
   * Otherwise, it renders the child components.
   */
  public render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            backgroundColor: '#333333', // A subtle background color
          }}
        >
          <Container maxWidth="sm">
            <Paper
              elevation={3}
              sx={{
                padding: 4,
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Tooltip title={"We're working on it!"}><BuildCircleOutlinedIcon sx={{ fontSize: 60, color: 'primary.main' }} /></Tooltip>
              <Box
                component="img"
                src="/logo.png"
                alt="Enantio Logo"
                sx={{
                  width: 150,
                  height: 150,
                  my: 2,
                }}
              />
              <Typography variant="h4" component="h1" gutterBottom>
                Something went wrong.
              </Typography>
              <Typography variant="body1" color="text.secondary">
                We're sorry for the inconvenience. The application has encountered an error.
                This may be temporary. Please try again.
              </Typography>
              <Button
                variant="contained"
                onClick={() => window.location.reload()}
                sx={{ mt: 2 }}
              >
                Try Again
              </Button>
            </Paper>
          </Container>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
